// ============================================================================
// COURT REMINDER SERVICE — Court date tracking + automated WhatsApp/Email reminders
// ============================================================================
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../firebase-config';
import { hoursUntilAccra, accraCalendarDateString, formatAccraDate } from '../utils/firmassistTime';

/**
 * Create a court date entry
 */
export const createCourtDate = async (firmId, courtDate) => {
  try {
    const docRef = await addDoc(collection(db, 'courtDates'), {
      firmId,
      caseId: courtDate.caseId || '',
      caseName: courtDate.caseName || '',
      caseNumber: courtDate.caseNumber || '',
      client: courtDate.client || '',
      court: courtDate.court || '',
      judge: courtDate.judge || '',
      type: courtDate.type || 'hearing',       // hearing, trial, ruling, mention, mediation, conference
      date: courtDate.date || '',               // ISO date string
      time: courtDate.time || '09:00',
      notes: courtDate.notes || '',
      lawyerAssigned: courtDate.lawyerAssigned || '',
      lawyerName: courtDate.lawyerName || '',
      status: courtDate.status || 'upcoming',   // upcoming, completed, adjourned, cancelled
      adjournedTo: courtDate.adjournedTo || '', // new date if adjourned
      adjournmentReason: courtDate.adjournmentReason || '',
      outcome: courtDate.outcome || '',         // for completed hearings
      reminders: {
        fortyEightHour: false,
        twentyFourHour: false,
        twoHour: false
      },
      reminderContacts: courtDate.reminderContacts || [],   // [{name, phone, email}]
      notifyClient: courtDate.notifyClient !== false,
      notifyLawyer: courtDate.notifyLawyer !== false,
      createdBy: courtDate.createdBy || '',
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now()
    });
    return { success: true, id: docRef.id };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/**
 * Get all court dates for a firm
 */
export const getCourtDates = async (firmId, filters = {}) => {
  try {
    const snap = await getDocs(query(
      collection(db, 'courtDates'),
      where('firmId', '==', firmId),
      orderBy('date', 'asc')
    ));
    let dates = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    if (filters.status) {
      dates = dates.filter(d => d.status === filters.status);
    }
    if (filters.lawyerAssigned) {
      dates = dates.filter(d => d.lawyerAssigned === filters.lawyerAssigned);
    }
    if (filters.caseId) {
      dates = dates.filter(d => d.caseId === filters.caseId);
    }
    if (filters.upcoming) {
      const today = accraCalendarDateString();
      dates = dates.filter(d => d.date >= today && d.status === 'upcoming');
    }

    return { success: true, dates };
  } catch (e) {
    return { success: false, dates: [], error: e.message };
  }
};

/**
 * Update a court date
 */
export const updateCourtDate = async (id, updates) => {
  try {
    await updateDoc(doc(db, 'courtDates', id), { ...updates, updatedAt: Timestamp.now() });
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/**
 * Delete a court date
 */
export const deleteCourtDate = async (id) => {
  try {
    await deleteDoc(doc(db, 'courtDates', id));
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/**
 * Check which court dates need reminders and return them
 * Called periodically (e.g. every 15 minutes) by the client
 */
export const checkPendingReminders = async (firmId) => {
  try {
    const result = await getCourtDates(firmId, { upcoming: true });
    if (!result.success) return { success: false, reminders: [] };

    const now = new Date();
    const reminders = [];

    for (const cd of result.dates) {
      // Africa/Accra business time (utils/firmassistTime.js) — a bare
      // "YYYY-MM-DDTHH:MM" string with no timezone designator is parsed as
      // the VIEWER'S OWN local time, which would shift every reminder
      // tier's timing by the browser's UTC offset. hoursUntilAccra anchors
      // it to Accra instead, so the 48h/24h/2h windows mean the same real
      // moment regardless of where the reminder is computed.
      const hoursUntil = hoursUntilAccra(cd.date, cd.time || '09:00', now);
      if (hoursUntil === null) continue;

      if (hoursUntil <= 48 && hoursUntil > 24 && !cd.reminders?.fortyEightHour) {
        reminders.push({ ...cd, reminderType: '48hour', hoursUntil: Math.round(hoursUntil) });
      }
      if (hoursUntil <= 24 && hoursUntil > 2 && !cd.reminders?.twentyFourHour) {
        reminders.push({ ...cd, reminderType: '24hour', hoursUntil: Math.round(hoursUntil) });
      }
      if (hoursUntil <= 2 && hoursUntil > 0 && !cd.reminders?.twoHour) {
        reminders.push({ ...cd, reminderType: '2hour', hoursUntil: Math.round(hoursUntil * 60) + ' minutes' });
      }
    }

    return { success: true, reminders };
  } catch (e) {
    return { success: false, reminders: [], error: e.message };
  }
};

/**
 * Mark a reminder as sent
 */
export const markReminderSent = async (courtDateId, reminderType) => {
  const fieldMap = {
    '48hour': 'reminders.fortyEightHour',
    '24hour': 'reminders.twentyFourHour',
    '2hour': 'reminders.twoHour'
  };
  try {
    await updateDoc(doc(db, 'courtDates', courtDateId), {
      [fieldMap[reminderType]]: true,
      updatedAt: Timestamp.now()
    });
    return { success: true };
  } catch (e) {
    return { success: false, error: e.message };
  }
};

/**
 * Generate a WhatsApp reminder message
 */
export const generateReminderMessage = (courtDate, reminderType) => {
  const timeLabel = reminderType === '48hour' ? '2 days' : reminderType === '24hour' ? '24 hours' : '2 hours';
  return `⚖️ *Court Reminder*\n\n📋 Case: ${courtDate.caseName || 'N/A'}\n🏛️ Court: ${courtDate.court || 'N/A'}\n📅 Date: ${formatAccraDate(courtDate.date, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}\n⏰ Time: ${courtDate.time || 'TBD'}\n${courtDate.judge ? `👨‍⚖️ Judge: ${courtDate.judge}\n` : ''}${courtDate.type ? `📌 Type: ${courtDate.type.charAt(0).toUpperCase() + courtDate.type.slice(1)}\n` : ''}\n⏳ This hearing is in *${timeLabel}*.\n\n💰 *Appearance Fee Reminder:* Please ensure the appearance fee for this court session has been paid before the hearing date.\n\n${courtDate.notes ? `📝 Notes: ${courtDate.notes}\n\n` : ''}Please arrive at least 30 minutes early with all relevant documents.\n\n— Sent via FirmAssist`;
};

// Court date types
export const COURT_DATE_TYPES = [
  { id: 'hearing', label: 'Hearing', icon: '⚖️' },
  { id: 'trial', label: 'Trial', icon: '🏛️' },
  { id: 'ruling', label: 'Ruling/Judgment', icon: '📜' },
  { id: 'mention', label: 'Mention', icon: '📋' },
  { id: 'mediation', label: 'Mediation/ADR', icon: '🤝' },
  { id: 'conference', label: 'Pre-trial Conference', icon: '👥' },
  { id: 'filing', label: 'Filing Deadline', icon: '📁' },
  { id: 'other', label: 'Other', icon: '📌' }
];

// Ghana courts
export const GHANA_COURTS = [
  'Supreme Court',
  'Court of Appeal',
  'High Court (General)',
  'High Court (Land Division)',
  'High Court (Commercial Division)',
  'High Court (Human Rights Division)',
  'High Court (Financial & Economic Crimes)',
  'Circuit Court',
  'District Court',
  'Magistrate Court',
  'Juvenile Court',
  'Family Tribunal',
  'National Labour Commission',
  'CHRAJ',
  'Rent Control',
  'Customary Land Secretariat',
  'Other'
];
